// @flow
import { OverlayModal } from 'components/templates/overlayModal';
import { OverlayPortal } from 'components/templates/overlayPortal';
import { chunk } from 'lodash';
import React, { useState } from 'react';
import Slider from 'react-slick';
import { ImageGalleryGrid } from 'components/molecules/imageGalleryGrid';
import { PaginationControl } from 'components/molecules/paginationControl';
import { useViewport } from 'components/templates/viewportProvider';
import type { GalleryImageType } from 'types/common';
// eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  images: Array<GalleryImageType>,
};

const IMAGES_PER_CHUNK = 5;

const ImageGallery = (props: Props) => {
  const { height } = useViewport();
  const [activeSlide, setActiveSlide] = useState(0);
  const [slider, setSlider] = useState();
  const [imageForOverlay, setImageForOverlay] = useState(null);

  const { images } = props;

  if (!images || images.length === 0) {
    return null;
  }

  const imageChunks: Array<Array<GalleryImageType>> = chunk(
    images,
    IMAGES_PER_CHUNK
  );

  const settings = {
    className: 'slider variable-width image-gallery',
    infinite: true,
    slidesToShow: 1.5,
    slidesToScroll: 1,
    variableWidth: true,
    speed: 1000,
    beforeChange: (current, next) => setActiveSlide(next),
    arrows: false,
    draggable: true,
    cssEase: 'cubic-bezier(0.77, 0, 0.175, 1)',
  };

  const renderContent = () => {
    return (
      <Slider ref={c => setSlider(c)} {...settings} style={{ height: '100%' }}>
        {imageChunks.map((imageChunk, idx) => {
          return (
            <ImageGalleryGrid
              data={imageChunk}
              onItemClick={onImageClick}
              key={idx}
            />
          );
        })}
      </Slider>
    );
  };

  const onImageClick = (image: GalleryImageType) => {
    setImageForOverlay(image);
  };

  const clearImageForOverlay = () => {
    setImageForOverlay(null);
  };

  // Render footer
  const next = () => {
    slider.slickNext();
  };

  const previous = () => {
    slider.slickPrev();
  };

  const renderFooter = () => {
    return (
      <div className="section-footer">
        <PaginationControl
          className={'section-footer-item'}
          theme={'dark-theme'}
          mode={'slider'}
          currentIndex={activeSlide + 1}
          total={imageChunks.length}
          onNextClick={next}
          onPreviousClick={previous}
        />
      </div>
    );
  };

  return (
    <div
      className={'section-container no-gutter gallery-desktop'}
      style={{ height }}
    >
      {renderContent()}
      {renderFooter()}
      {imageForOverlay && (
        <OverlayPortal>
          <OverlayModal
            activeImage={imageForOverlay}
            images={images}
            theme={'dark-theme'}
            type={'image-overlay'}
            onClosePress={clearImageForOverlay}
            breadcrumbList={[
              {
                breadcrumbName: 'Gallery',
                breadcrumbLink: '#',
              },
            ]}
            hideGutter={true}
          />
        </OverlayPortal>
      )}
    </div>
  );
};

export default ImageGallery;
